import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Clock, User, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { blogPosts } from "../data/blogPosts";
import { useState, useMemo } from "react";

const categories = ["All", "Educational", "Commercial", "Local", "How-To", "Seasonal", "Industry Guide"];
const POSTS_PER_PAGE = 9;

export default function Blog() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const filtered = useMemo(() => {
    let posts = activeCategory === "All" ? blogPosts : blogPosts.filter(p => p.category === activeCategory);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      posts = posts.filter(p =>
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        p.tags.some(t => t.toLowerCase().includes(q))
      );
    }
    return posts.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [activeCategory, searchQuery]);

  const totalPages = Math.ceil(filtered.length / POSTS_PER_PAGE);
  const paginatedPosts = filtered.slice((currentPage - 1) * POSTS_PER_PAGE, currentPage * POSTS_PER_PAGE);

  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const getCategoryCount = (cat) => {
    if (cat === "All") return blogPosts.length;
    return blogPosts.filter(p => p.category === cat).length;
  };

  const blogListSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Ninja Screen Print Blog",
    "description": "Expert guides on screen printing, DTF, embroidery, and custom apparel for Dallas-Fort Worth businesses.",
    "url": "https://ninjascreenprint.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "Ninja Screen Print",
      "url": "https://ninjascreenprint.com"
    },
    "blogPost": filtered.slice(0, 10).map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.metaDescription,
      "datePublished": post.date,
      "url": `https://ninjascreenprint.com/blog/${post.slug}`
    }))
  };

  return (
    <>
      <Helmet>
        <title>Screen Printing Blog Dallas TX | Custom Apparel Tips & Guides | Ninja Screen Print</title>
        <meta name="description" content="Screen printing blog with expert guides, pricing tips, fabric advice, and Dallas market insights. From Ninja Screen Print — Dallas's #1 custom apparel printer." />
        <script type="application/ld+json">{JSON.stringify(blogListSchema)}</script>
      </Helmet>

      {/* Hero */}
      <div className="pt-16 bg-[#0d0d0d] border-b border-neutral-800">
        <div className="ninja-container py-16">
          <div className="red-line" />
          <h1 className="font-teko text-5xl sm:text-6xl text-white uppercase tracking-tight">
            Screen Printing Insights
          </h1>
          <p className="text-neutral-400 font-inter mt-3 max-w-2xl text-lg">
            Expert guides on screen printing, DTF, embroidery, and custom apparel for Dallas businesses, schools, and events.
          </p>
          <div className="mt-4 flex items-center gap-2 text-sm text-neutral-500 font-inter">
            <span data-testid="post-count" className="bg-red-600/20 text-red-400 px-3 py-1 border border-red-600/30">{blogPosts.length} Articles</span>
            <span>Updated weekly with DFW market insights</span>
          </div>
        </div>
      </div>

      {/* Search + Category Filter */}
      <div className="sticky top-16 z-40 bg-[#0A0A0A]/95 backdrop-blur border-b border-neutral-800" data-testid="blog-filter">
        <div className="ninja-container py-3 space-y-3">
          {/* Search */}
          <div className="relative max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              type="text"
              data-testid="blog-search"
              value={searchQuery}
              onChange={handleSearch}
              placeholder="Search articles..."
              className="w-full bg-neutral-900 border border-neutral-800 text-white text-sm font-inter pl-9 pr-4 py-2 focus:border-red-600 focus:outline-none transition-colors"
            />
          </div>
          {/* Categories */}
          <div className="flex gap-2 overflow-x-auto pb-1">
            {categories.map((cat) => (
              <button
                key={cat}
                data-testid={`filter-${cat}`}
                onClick={() => handleCategoryChange(cat)}
                className={`px-4 py-1.5 text-xs uppercase tracking-widest font-inter whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                  activeCategory === cat
                    ? "bg-red-600 text-white"
                    : "border border-neutral-700 text-neutral-400 hover:text-white hover:border-neutral-500"
                }`}
              >
                {cat}
                <span className={`text-[10px] ${activeCategory === cat ? "text-red-200" : "text-neutral-600"}`}>
                  ({getCategoryCount(cat)})
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Posts Grid */}
      <section className="ninja-section">
        <div className="ninja-container">
          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-teko text-3xl text-neutral-500 uppercase">No articles found</p>
              <p className="text-neutral-600 font-inter text-sm mt-2">Try adjusting your search or filter.</p>
            </div>
          ) : (
            <>
              {/* Featured Post (first page only, no search active) */}
              {currentPage === 1 && !searchQuery.trim() && activeCategory === "All" && filtered.length > 0 && (
                <Link
                  to={`/blog/${filtered[0].slug}`}
                  data-testid="featured-post"
                  className="ninja-card group flex flex-col lg:flex-row overflow-hidden mb-8"
                >
                  <div className="blog-img-wrap lg:w-1/2 h-64 lg:h-auto overflow-hidden">
                    <img src={filtered[0].image} alt={filtered[0].title} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="p-8 lg:w-1/2 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="bg-red-600 text-white text-xs uppercase tracking-widest px-3 py-1 font-inter">Featured</span>
                      <span className="text-xs text-red-500 uppercase tracking-widest font-inter">{filtered[0].category}</span>
                    </div>
                    <h2 className="font-teko text-3xl sm:text-4xl text-white uppercase tracking-tight group-hover:text-red-400 transition-colors mb-3">{filtered[0].title}</h2>
                    <p className="text-neutral-400 font-inter text-sm leading-relaxed mb-4">{filtered[0].excerpt}</p>
                    <div className="flex items-center gap-4 text-xs text-neutral-500 font-inter mb-4">
                      <span className="flex items-center gap-1"><User size={10} /> {filtered[0].author}</span>
                      <span className="flex items-center gap-1"><Clock size={10} /> {filtered[0].readTime}</span>
                      <span>{filtered[0].date}</span>
                    </div>
                    <div className="flex items-center gap-2 text-red-500 text-sm font-inter uppercase tracking-widest group-hover:gap-3 transition-all">
                      Read Article <ArrowRight size={14} />
                    </div>
                  </div>
                </Link>
              )}

              {/* Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {(currentPage === 1 && !searchQuery.trim() && activeCategory === "All" ? paginatedPosts.slice(1) : paginatedPosts).map((post, i) => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    data-testid={`blog-post-${post.slug}`}
                    className="ninja-card group overflow-hidden flex flex-col"
                  >
                    <div className="blog-img-wrap h-48 overflow-hidden">
                      <img src={post.image} alt={post.title} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-red-500 uppercase tracking-widest font-inter">{post.category}</span>
                        <span className="text-neutral-700">&bull;</span>
                        <span className="text-xs text-neutral-600 font-inter">{post.readTime}</span>
                      </div>
                      <h2 className="font-teko text-xl text-white uppercase tracking-wide leading-tight group-hover:text-red-400 transition-colors mb-2 flex-1">{post.title}</h2>
                      <p className="text-neutral-500 font-inter text-xs leading-relaxed line-clamp-3 mb-4">{post.excerpt}</p>
                      <div className="flex items-center justify-between mt-auto">
                        <span className="text-xs text-neutral-600 font-inter">{post.date}</span>
                        <div className="flex items-center gap-2 text-red-500 text-xs font-inter uppercase tracking-widest group-hover:gap-3 transition-all">
                          Read More <ArrowRight size={12} />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-12" data-testid="blog-pagination">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    data-testid="pagination-prev"
                    className="p-2 border border-neutral-700 text-neutral-400 hover:text-white hover:border-neutral-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      data-testid={`pagination-page-${page}`}
                      className={`w-9 h-9 text-sm font-inter transition-colors ${
                        currentPage === page
                          ? "bg-red-600 text-white"
                          : "border border-neutral-700 text-neutral-400 hover:text-white hover:border-neutral-500"
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    data-testid="pagination-next"
                    className="p-2 border border-neutral-700 text-neutral-400 hover:text-white hover:border-neutral-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Content Calendar Preview */}
      <section className="py-16 bg-[#0d0d0d] border-t border-neutral-800" data-testid="content-calendar">
        <div className="ninja-container">
          <div className="red-line" />
          <h2 className="font-teko text-4xl text-white uppercase tracking-tight mb-2">Content Calendar</h2>
          <p className="text-neutral-400 font-inter text-sm mb-8">We publish new DFW-focused content weekly. Here's what's coming up.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { month: "Weekly", topic: "Screen Printing Tips & Tricks", type: "Educational" },
              { month: "Bi-Weekly", topic: "DFW Business Spotlights", type: "Local" },
              { month: "Monthly", topic: "Seasonal Apparel Guides", type: "Seasonal" },
              { month: "Monthly", topic: "Industry Deep Dives", type: "Industry Guide" },
            ].map((item, i) => (
              <div key={i} className="ninja-card p-5" data-testid={`calendar-item-${i}`}>
                <div className="text-xs text-red-500 uppercase tracking-widest font-inter mb-1">{item.month}</div>
                <h3 className="font-teko text-lg text-white uppercase tracking-wide mb-1">{item.topic}</h3>
                <span className="text-xs text-neutral-500 font-inter">{item.type}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 border-t border-neutral-800">
        <div className="ninja-container text-center">
          <h2 className="font-teko text-4xl text-white uppercase tracking-tight">Ready to Order Custom Apparel?</h2>
          <p className="text-neutral-400 font-inter mt-2 mb-6">Free quote in 2 hours. Free DFW delivery. Fast turnaround.</p>
          <Link to="/contact" data-testid="blog-cta-btn" className="ninja-btn-primary font-teko tracking-widest text-lg px-10 py-4">GET A FREE QUOTE</Link>
        </div>
      </section>
    </>
  );
}
